import type { League } from '@h2h/shared';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { Check, Lock, Plus } from '../../components/ui/icons';
import { useCurrentRound } from '../../hooks/useQueries';
import type { LeagueIdentity } from '../../data/leagues';
import { cn } from '../../lib/cn';
import { JoinedPill } from './Badges';
import { formatWaitingSubtitle } from './helpers';

export function FootballLeagueRow({
  identity,
  league,
  joined,
  live,
  subtitle: subtitleProp,
  onClick,
  disabled
}: {
  identity: LeagueIdentity;
  league?: League;
  joined: boolean;
  live?: boolean;
  subtitle?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const { data: round } = useCurrentRound(
    live ? identity.leagueId : undefined,
    live ? (league?.currentRoundId ?? null) : null
  );
  const Tag = disabled ? 'div' : 'button';

  const subtitle =
    live && round
      ? `Live now · ${round.label}`
      : (subtitleProp ?? formatWaitingSubtitle(identity));

  return (
    <Tag
      type={disabled ? undefined : 'button'}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-[16px] p-[11px_12px] text-left',
        live
          ? 'border-[1.5px] border-accent/50 bg-[#F0FBF5]'
          : 'border border-black/[0.07] bg-white',
        !disabled && 'tap cursor-pointer'
      )}
    >
      <NationalFlagEmblem leagueId={identity.leagueId} country={identity.country} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-bold tracking-snug text-ink">{league?.name ?? identity.name}</div>
        <div
          className={cn(
            'mt-0.5 flex items-center gap-[5px] text-[11.5px] font-semibold',
            live ? 'font-bold text-accent-ink' : 'text-ink2'
          )}
        >
          {live ? (
            <>
              <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
              {subtitle}
            </>
          ) : (
            subtitle
          )}
        </div>
      </div>
      {live && joined ? (
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent shadow-[0_4px_10px_-4px_rgba(36,197,106,0.7)]">
          <Check width={13} height={13} strokeWidth={3.4} className="text-white" />
        </div>
      ) : joined ? (
        <JoinedPill />
      ) : disabled ? (
        <Lock width={14} height={14} strokeWidth={2.4} className="shrink-0 text-ink2" />
      ) : (
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-black/[0.06] bg-[#F2F2F6]">
          <Plus width={13} height={13} strokeWidth={2.6} className="text-ink2" />
        </div>
      )}
    </Tag>
  );
}
